(() => {
  const purchaseStateRestorers = new WeakMap();

  const formatMoney = (cents, currency, locale) => {
    try {
      const remainder = Math.abs(cents) % 100;
      const minimumFractionDigits = remainder === 0 ? 0 : remainder % 10 === 0 ? 1 : 2;
      return new Intl.NumberFormat(locale || document.documentElement.lang || window.navigator.language, {
        style: 'currency',
        currency,
        minimumFractionDigits,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2)} ${currency}`.trim();
    }
  };

  const initialize = (root) => {
    const products = root.matches?.('[data-event-product]')
      ? [root]
      : Array.from(root.querySelectorAll?.('[data-event-product]') || []);

    products.forEach((product) => {
      if (product.dataset.eventProductReady === 'true') return;
      product.dataset.eventProductReady = 'true';
      product.spiritHostObservers = [];
      const purchaseBar = product.querySelector('.spirit-event-purchase, .spirit-product-sticky-bar');
      if (purchaseBar) {
        const main = document.getElementById('MainContent');
        const purchaseSize = new ResizeObserver(() => {
          const rect = purchaseBar.getBoundingClientRect();
          main?.style.setProperty('--spirit-purchase-space', `${Math.ceil(rect.height + Math.max(0, window.innerHeight - rect.bottom) + 24)}px`);
        });
        purchaseSize.observe(purchaseBar);
        product.spiritHostObservers.push(purchaseSize);
      }

      const wishlist = product.querySelector('[data-event-wishlist]');
      const wishlistKey = `spirit-event-wishlist:${product.dataset.productId}`;
      try {
        wishlist?.setAttribute('aria-pressed', String(window.localStorage.getItem(wishlistKey) === 'true'));
      } catch (_error) {
        // Storage can be unavailable in privacy-restricted browser contexts.
      }
      wishlist?.addEventListener('click', () => {
        const nextState = wishlist.getAttribute('aria-pressed') !== 'true';
        wishlist.setAttribute('aria-pressed', String(nextState));
        try {
          window.localStorage.setItem(wishlistKey, String(nextState));
        } catch (_error) {
          // The pressed state still works for the current page when storage is unavailable.
        }
      });

      product.querySelectorAll('[data-feature-media-id]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const mediaId = trigger.dataset.featureMediaId;
          const gallery = product.querySelector('spirit-product-gallery');
          const matchingOpener = gallery?.querySelector(`[data-gallery-media-id="${CSS.escape(mediaId)}"]`);
          const fallbackOpener = gallery?.querySelector('[data-gallery-open]');
          const opener = matchingOpener || fallbackOpener;
          const galleryIndex = Number(opener?.dataset.galleryOpen);
          if (gallery && opener && Number.isFinite(galleryIndex) && typeof gallery.open === 'function') {
            gallery.open(galleryIndex, trigger);
          } else {
            opener?.click();
          }
        });
      });

      const form = product.querySelector('[data-event-product-form]');
      const submitButton = form?.querySelector('button[type="submit"]');
      const formMessage = form?.querySelector('[data-event-form-message]');
      const variantInput = form?.querySelector('[data-main-variant-id]');
      const quantityInput = form?.querySelector('[data-event-quantity]');
      const variantSelect = product.querySelector('[data-event-variant-select]');
      const total = product.querySelector('[data-event-total-price]');
      const addonInputs = Array.from(product.querySelectorAll('[data-addon-input]'));
      const optionInputs = Array.from(new Set([
        ...addonInputs,
        ...product.querySelectorAll('input[name="spirit-lodging"]'),
      ]));
      const includedOption = product.querySelector('input[name="spirit-lodging"][value=""]');
      const summary = product.querySelector('[data-event-summary]');
      let purchasePending = false;
      const dateHasPassed = (choice) => {
        const date = choice?.dataset.eventDate?.slice(0, 10);
        if (!date) return false;
        // Compare calendar days in the shop's UTC offset, not the visitor's timezone.
        const offset = product.dataset.eventShopOffset || '+0000';
        const minutes = (Number(offset.slice(1, 3)) * 60 + Number(offset.slice(3, 5))) * (offset[0] === '-' ? -1 : 1);
        const today = new Date(Date.now() + minutes * 60000).toISOString().slice(0, 10);
        return date < today;
      };
      const selectedChoice = () => variantSelect ? variantSelect.selectedOptions[0] : submitButton;
      const canPurchase = () => {
        const choice = selectedChoice();
        return choice?.dataset.available === 'true' && !dateHasPassed(choice) && (!variantSelect || !choice.disabled);
      };
      const updateSubmitButton = () => {
        if (!submitButton) return;
        const available = canPurchase();
        submitButton.disabled = purchasePending || !available;
        if (purchasePending) {
          submitButton.setAttribute('aria-busy', 'true');
        } else {
          submitButton.removeAttribute('aria-busy');
        }
        submitButton.textContent = available
          ? submitButton.dataset.availableLabel
          : dateHasPassed(selectedChoice()) ? product.dataset.eventExpiredLabel : submitButton.dataset.soldOutLabel;
      };
      const getPurchaseAction = () =>
        product.dataset.purchaseAction === 'buy_now' ? 'buy_now' : 'add_to_cart';
      const getPurchaseRedirectUrl = () =>
        getPurchaseAction() === 'buy_now' ? product.dataset.checkoutUrl : product.dataset.cartUrl;
      const updateTotal = () => {
        if (!total) return;
        const pricing = total.closest('[data-spirit-price]');
        const option = variantSelect?.selectedOptions[0];
        if (option && window.SpiritPricing) {
          window.SpiritPricing.setVariant(product.dataset.productId, Number(option.dataset.price), Number(option.dataset.compareAtPrice));
        }
        const basePrice = pricing && window.SpiritPricing
          ? window.SpiritPricing.unitPrice(pricing)
          : Number(option?.dataset.price || total.dataset.basePrice || 0);
        const selectedAddons = addonInputs.filter((input) => input.checked && !input.disabled);
        const addonsPrice = selectedAddons.reduce((sum, input) => sum + Number(input.dataset.addonPrice || 0), 0);
        const quantity = Math.max(Number(quantityInput?.value || 1), 1);
        total.textContent = formatMoney(
          (basePrice + addonsPrice) * quantity,
          total.dataset.currency,
          product.dataset.locale
        );
        if (pricing) {
          pricing.querySelector('[data-price-regular]').textContent = formatMoney(
            (Number(pricing.dataset.regularPrice) + addonsPrice) * quantity,
            total.dataset.currency,
            product.dataset.locale
          );
        }

        const addonSummary = product.querySelector('[data-event-addon-summary]');
        if (addonSummary) {
          addonSummary.replaceChildren(...selectedAddons.map((input) => {
            const item = document.createElement('li');
            const amount = formatMoney(Number(input.dataset.addonPrice || 0) * quantity, total.dataset.currency, product.dataset.locale);
            item.textContent = `${input.dataset.eventOptionSummary || ''}: +${amount}`;
            return item;
          }));
          addonSummary.hidden = selectedAddons.length === 0;
        }
      };

      document.addEventListener('spirit:prices-updated', updateTotal);

      const updateVariant = () => {
        const selectedOption = variantSelect?.selectedOptions[0];
        if (!selectedOption) return;

        if (variantInput) variantInput.value = selectedOption.value;
        if (total) total.dataset.basePrice = selectedOption.dataset.price || '0';
        if (quantityInput) {
          const minimum = Math.max(Number(selectedOption.dataset.quantityMin || 1), 1);
          const increment = Math.max(Number(selectedOption.dataset.quantityIncrement || 1), 1);
          const maximum = Number(selectedOption.dataset.quantityMax || 0);
          const current = Math.max(Number(quantityInput.value || minimum), minimum);
          let nextQuantity = minimum + Math.floor((current - minimum) / increment) * increment;
          if (maximum > 0) nextQuantity = Math.min(nextQuantity, maximum);
          quantityInput.min = String(minimum);
          quantityInput.dataset.min = String(minimum);
          quantityInput.step = String(increment);
          if (maximum > 0) {
            quantityInput.max = String(maximum);
            quantityInput.dataset.max = String(maximum);
          } else {
            quantityInput.removeAttribute('max');
            delete quantityInput.dataset.max;
          }
          quantityInput.value = String(nextQuantity);
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (includedOption && selectedOption.dataset.summary) {
          includedOption.dataset.eventOptionSummary = selectedOption.dataset.summary;
        }
        if (
          summary &&
          !summary.hasAttribute('data-event-summary-fixed') &&
          selectedOption.dataset.summary
        ) {
          summary.textContent = selectedOption.dataset.summary;
        }
        updateSubmitButton();
        if (formMessage) formMessage.textContent = '';

        const url = new URL(window.location.href);
        url.searchParams.set('variant', selectedOption.value);
        window.history.replaceState({}, '', url);
        updateTotal();
      };

      optionInputs.forEach((input) => input.addEventListener('change', updateTotal));
      quantityInput?.addEventListener('input', updateTotal);
      quantityInput?.addEventListener('change', updateTotal);
      variantSelect?.addEventListener('change', updateVariant);
      const restoreVariant = () => {
        if (variantSelect && dateHasPassed(selectedChoice())) {
          const next = Array.from(variantSelect.options)
            .filter(option => option.dataset.available === 'true' && !dateHasPassed(option) && !option.disabled)
            .sort((a, b) => (a.dataset.eventDate || '').localeCompare(b.dataset.eventDate || ''))[0];
          if (next) variantSelect.value = next.value;
        }
        if (variantSelect) updateVariant();
        else {
          updateTotal();
          updateSubmitButton();
        }
      };
      purchaseStateRestorers.set(product, (restoredFromCache) => {
        if (restoredFromCache) purchasePending = false;
        restoreVariant();
      });
      restoreVariant();

      product.querySelectorAll('[data-host-track]').forEach((track) => {
        const hostsBlock = track.closest('.spirit-event-hosts');
        const previousButton = hostsBlock?.querySelector('[data-host-scroll-prev]');
        const nextButton = hostsBlock?.querySelector('[data-host-scroll-next]');
        const cards = Array.from(track.querySelectorAll('.spirit-event-host-card'));
        if (!previousButton || !nextButton || cards.length < 2) return;

        const syncButtons = () => {
          const trackRect = track.getBoundingClientRect();
          const hasOverflow = track.scrollWidth > track.clientWidth + 2;
          const canScrollLeft = hasOverflow && cards.some((card) => card.getBoundingClientRect().left < trackRect.left - 2);
          const canScrollRight = hasOverflow && cards.some((card) => card.getBoundingClientRect().right > trackRect.right + 2);

          previousButton.hidden = !hasOverflow;
          nextButton.hidden = !hasOverflow;
          previousButton.disabled = !hasOverflow || !canScrollLeft;
          nextButton.disabled = !hasOverflow || !canScrollRight;
          previousButton.setAttribute('aria-disabled', String(previousButton.disabled));
          nextButton.setAttribute('aria-disabled', String(nextButton.disabled));
        };

        const scrollByCard = (step) => {
          const trackRect = track.getBoundingClientRect();
          const candidates = cards
            .map((card) => ({ card, rect: card.getBoundingClientRect() }))
            .filter(({ rect }) =>
              step < 0 ? rect.left < trackRect.left - 2 : rect.right > trackRect.right + 2
            )
            .sort((first, second) =>
              step < 0 ? second.rect.right - first.rect.right : first.rect.left - second.rect.left
            );
          const target = candidates[0]?.card;
          if (!target) return;

          target.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        };

        previousButton.addEventListener('click', () => scrollByCard(-1));
        nextButton.addEventListener('click', () => scrollByCard(1));
        track.addEventListener('scroll', () => requestAnimationFrame(syncButtons), { passive: true });
        const observer = new ResizeObserver(syncButtons);
        observer.observe(track);
        product.spiritHostObservers.push(observer);
        requestAnimationFrame(syncButtons);
      });

      form?.addEventListener('submit', async (event) => {
        if (!canPurchase()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          updateSubmitButton();
          if (formMessage) formMessage.textContent = submitButton?.textContent || product.dataset.eventExpiredLabel;
          return;
        }
        const selectedAddons = addonInputs.filter((input) => input.checked && !input.disabled);
        const isBuyNow = getPurchaseAction() === 'buy_now';
        if (!isBuyNow && !selectedAddons.length) return;

        event.preventDefault();
        if (isBuyNow) event.stopImmediatePropagation();
        if (purchasePending) return;
        const mainVariant = variantInput?.value || variantSelect?.value;
        if (!mainVariant) return;

        purchasePending = true;
        updateSubmitButton();
        if (formMessage) formMessage.textContent = '';

        try {
          if (isBuyNow) {
            const clearResponse = await fetch(product.dataset.cartClearUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({}),
            });
            const clearResponseBody = await clearResponse.json().catch(() => ({}));
            if (!clearResponse.ok) {
              throw new Error(
                clearResponseBody.description || clearResponseBody.message || product.dataset.cartErrorLabel
              );
            }
          }

          const items = [
            { id: Number(mainVariant), quantity: Math.max(Number(quantityInput?.value || 1), 1) },
            ...selectedAddons.map((input) => ({
              id: Number(input.value),
              quantity: Math.max(Number(quantityInput?.value || 1), 1),
            })),
          ];
          const response = await fetch(product.dataset.cartAddUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const responseBody = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(responseBody.description || responseBody.message || product.dataset.cartErrorLabel);
          }
          window.location.assign(getPurchaseRedirectUrl());
        } catch (error) {
          console.error(error);
          if (formMessage) formMessage.textContent = error.message;
          purchasePending = false;
          updateSubmitButton();
        }
      }, true);
    });
  };

  const scrollRelatedEvents = (button) => {
    const relatedEvents = button.closest('[data-related-events]');
    const track = relatedEvents?.querySelector('[data-related-track]');
    const cards = Array.from(track?.querySelectorAll('.grid__item') || []);
    if (!track || cards.length < 2) return;

    const trackRect = track.getBoundingClientRect();
    const rtl = getComputedStyle(track).direction === 'rtl';
    const anchor = rtl ? trackRect.right : trackRect.left;
    let currentIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const rect = card.getBoundingClientRect();
      const edge = rtl ? rect.right : rect.left;
      const distance = Math.abs(edge - anchor);
      if (distance < closestDistance) {
        currentIndex = index;
        closestDistance = distance;
      }
    });

    const step = Number(button.dataset.relatedScroll || 0);
    const targetIndex = Math.min(Math.max(currentIndex + step, 0), cards.length - 1);
    cards[targetIndex].scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-related-scroll]');
    if (button) scrollRelatedEvents(button);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document));
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  const restorePurchaseState = (event) => {
    // History navigation can restore form values after pageshow/popstate, without change events.
    setTimeout(() => {
      document.querySelectorAll('[data-event-product]').forEach((product) => {
        purchaseStateRestorers.get(product)?.(event.type === 'pageshow' && event.persisted);
      });
    }, 0);
  };
  window.addEventListener('pageshow', restorePurchaseState);
  window.addEventListener('popstate', restorePurchaseState);

  document.addEventListener('shopify:section:unload', (event) => {
    const products = event.target.matches?.('[data-event-product]')
      ? [event.target]
      : Array.from(event.target.querySelectorAll?.('[data-event-product]') || []);
    products.forEach((product) => {
      purchaseStateRestorers.delete(product);
      product.spiritHostObservers?.forEach((observer) => observer.disconnect());
      product.spiritHostObservers = [];
      if (product.querySelector('.spirit-event-purchase, .spirit-product-sticky-bar')) {
        document.getElementById('MainContent')?.style.removeProperty('--spirit-purchase-space');
      }
    });
  });
})();
